import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { Logo } from '@/components/layout/Logo';
import { Button } from '@/components/ui/Button';
import { authApi } from '@/api/endpoints';
import { useToastStore } from '@/store/toast';
import { getApiErrorMessage } from '@/api/client';
import { useSeo } from '@/hooks/useSeo';

const CODE_RE = /^[0-9]{6}$/;

export function ResetPassword() {
  // Shaxsiy/JS-only sahifa: indekslanmaydi; oldingi sahifaning JSON-LD i tozalanadi
  useSeo({ title: "Yangi parol — SQLMaster.uz", noindex: true });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const push = useToastStore((s) => s.push);

  const email = (location.state as { email?: string } | null)?.email ?? '';

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors] = useState<{ code?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) navigate('/forgot-password', { replace: true });
  }, [email, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!CODE_RE.test(code)) next.code = t('auth.errorCode');
    if (password.length < 8) next.password = t('auth.errorPasswordShort');
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setLoading(true);
    try {
      await authApi.resetPassword(email, code, password);
      push(t('auth.passwordReset'), 'success');
      navigate('/login');
    } catch (err) {
      push(getApiErrorMessage(err, t('auth.errorGeneric')), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-8 flex justify-center">
        <Logo />
      </div>

      <div className="card p-6 sm:p-8">
        <h1 className="mb-1 text-2xl font-bold">{t('auth.resetTitle')}</h1>
        <p className="mb-6 text-sm text-content-secondary">
          {t('auth.resetSubtitle')} <span className="font-medium text-content">{email}</span>
        </p>

        <form onSubmit={submit} noValidate className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('auth.verifyCodeLabel')}</label>
            <input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="input text-center text-2xl tracking-[0.5em]"
              autoFocus
            />
            {errors.code && <p className="mt-1 text-xs text-[var(--error)]">{errors.code}</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">{t('auth.newPassword')}</label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted" />
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                className="input pl-10 pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted hover:text-content"
                aria-label={t('auth.password')}
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-xs text-[var(--error)]">{errors.password}</p>}
          </div>

          <Button type="submit" loading={loading} className="w-full">
            {t('auth.resetButton')}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-content-secondary">
          <Link to="/login" className="font-medium text-brand-600 hover:underline">
            {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
